import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, DatePicker, Divider, Modal, Select } from 'antd'
import { DownloadOutlined, LoadingOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { toastError, toastSuccess } from '../../utils/toast'
import { apiGetReportStatus, apiGetReportDownload } from '../../api'
import { filterOptionByLabel } from '../../utils/filter-utils'

interface ReportDownloadModalProps {
    title: string
    open: boolean
    onClose: () => void
    companyOptions: { label: string; value: string }[]
    createReportApi: (params: any) => Promise<any>
    buildParams: (form: { cpCode: string; type: string; fromDate: string; toDate: string; date: string }) => any
}

const ReportDownloadModal = ({ title, open, onClose, companyOptions, createReportApi, buildParams }: ReportDownloadModalProps) => {
    const [reportCpCode, setReportCpCode] = useState<string>('')
    const [reportType, setReportType] = useState<string>('DATE')
    const [reportFromDate, setReportFromDate] = useState<string>(dayjs().format('YYYY-MM-DD'))
    const [reportToDate, setReportToDate] = useState<string>(dayjs().format('YYYY-MM-DD'))
    const [reportDate, setReportDate] = useState<string>('')
    const [reportStatus, setReportStatus] = useState<'idle' | 'polling' | 'success' | 'failed'>('idle')
    const [reportError, setReportError] = useState<string>('')
    const pollingRef = useRef<any>(null)
    const pollingTimeoutRef = useRef<any>(null)

    const stopPolling = useCallback(() => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current)
            pollingRef.current = null
        }
        if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current)
            pollingTimeoutRef.current = null
        }
    }, [])

    useEffect(() => {
        return () => stopPolling()
    }, [stopPolling])

    const resetForm = () => {
        stopPolling()
        setReportCpCode('')
        setReportType('DATE')
        setReportDate('')
        setReportFromDate(dayjs().format('YYYY-MM-DD'))
        setReportToDate(dayjs().format('YYYY-MM-DD'))
        setReportStatus('idle')
        setReportError('')
    }

    const handleClose = () => {
        resetForm()
        onClose()
    }

    const handleCreateReport = async () => {
        if (!reportCpCode || !reportType) {
            toastError('Vui lòng nhập đầy đủ thông tin!')
            return
        }
        if (reportType === 'DATE' && (!reportFromDate || !reportToDate)) {
            toastError('Vui lòng chọn ngày bắt đầu và ngày kết thúc!')
            return
        }
        if (reportType !== 'DATE' && !reportDate) {
            toastError('Vui lòng chọn thời gian!')
            return
        }

        try {
            setReportStatus('polling')
            setReportError('')
            const params = buildParams({
                cpCode: reportCpCode,
                type: reportType,
                fromDate: reportFromDate,
                toDate: reportToDate,
                date: reportDate,
            })
            const resp = await createReportApi(params)
            const reportId = resp.data?.reportId
            if (!reportId) {
                setReportStatus('failed')
                setReportError('Không nhận được mã báo cáo')
                return
            }

            const retryAfterMs = (resp.data?.retryAfterSeconds || 3) * 1000
            pollingRef.current = setInterval(async () => {
                try {
                    const statusResp = await apiGetReportStatus({ reportId, cpCode: reportCpCode })
                    const status = statusResp.data?.status

                    if (status === 'SUCCESS') {
                        stopPolling()
                        try {
                            const dlResp = await apiGetReportDownload({ reportId, cpCode: reportCpCode })
                            if (dlResp.data?.downloadUrl) {
                                window.open(dlResp.data.downloadUrl, '_blank')
                                setReportStatus('success')
                                toastSuccess('Tải báo cáo thành công!')
                            } else {
                                setReportStatus('failed')
                                setReportError('Không lấy được link tải xuống')
                            }
                        } catch {
                            setReportStatus('failed')
                            setReportError('Lỗi khi lấy link tải xuống')
                        }
                    } else if (status === 'FAILED') {
                        stopPolling()
                        setReportStatus('failed')
                        setReportError(statusResp.data?.error || 'Tạo báo cáo thất bại')
                    }
                } catch {
                    stopPolling()
                    setReportStatus('failed')
                    setReportError('Lỗi khi kiểm tra trạng thái báo cáo')
                }
            }, retryAfterMs)

            pollingTimeoutRef.current = setTimeout(() => {
                stopPolling()
                setReportStatus('failed')
                setReportError('Quá thời gian chờ (3 phút). Vui lòng thử lại.')
            }, 180000)
        } catch {
            setReportStatus('failed')
            setReportError('Có lỗi xảy ra khi tạo báo cáo!')
        }
    }

    const getDatePicker = () => {
        if (reportType === 'MONTH') return 'month'
        if (reportType === 'QUARTER') return 'quarter'
        return 'date'
    }

    const getDateFormat = () => {
        if (reportType === 'MONTH') return 'YYYY-MM'
        if (reportType === 'QUARTER') return 'YYYY-[Q]Q'
        return 'YYYY-MM-DD'
    }

    return (
        <Modal
            title={title}
            open={open}
            onCancel={handleClose}
            footer={reportStatus === 'idle' ? [
                <Button key="create" onClick={handleCreateReport} style={{ background: '#008965', color: 'white' }}>
                    Tạo báo cáo
                </Button>,
                <Button key="close" onClick={handleClose} style={{ background: 'gray', color: 'white' }}>
                    Đóng
                </Button>,
            ] : [
                <Button key="close" onClick={handleClose} style={{ background: 'gray', color: 'white' }}>
                    Đóng
                </Button>,
            ]}
        >
            <Divider />
            {reportStatus === 'idle' && (
                <>
                    <p>Chọn công ty</p>
                    <Select
                        style={{ width: '100%' }}
                        showSearch
                        placeholder="Chọn công ty"
                        options={companyOptions}
                        value={reportCpCode || undefined}
                        onChange={(value) => setReportCpCode(value)}
                        filterOption={filterOptionByLabel}
                    />
                    <p>Loại báo cáo</p>
                    <Select
                        style={{ width: '100%' }}
                        value={reportType}
                        onChange={(value) => {
                            setReportType(value)
                            setReportDate('')
                            setReportFromDate(dayjs().format('YYYY-MM-DD'))
                            setReportToDate(dayjs().format('YYYY-MM-DD'))
                        }}
                        options={[
                            { label: 'Theo ngày', value: 'DATE' },
                            { label: 'Theo tháng', value: 'MONTH' },
                            { label: 'Theo quý', value: 'QUARTER' },
                        ]}
                    />
                    {reportType === 'DATE' ? (
                        <>
                            <p>Chọn khoảng thời gian</p>
                            <DatePicker.RangePicker
                                style={{ width: '100%' }}
                                format="YYYY-MM-DD"
                                value={[dayjs(reportFromDate), dayjs(reportToDate)]}
                                allowClear={false}
                                onChange={(_, dateStrings) => {
                                    setReportFromDate(dateStrings[0] as string)
                                    setReportToDate(dateStrings[1] as string)
                                }}
                            />
                        </>
                    ) : (
                        <>
                            <p>Chọn thời gian</p>
                            <DatePicker
                                style={{ width: '100%' }}
                                picker={getDatePicker() as any}
                                format={getDateFormat()}
                                onChange={(_, dateString) => setReportDate(dateString as string)}
                            />
                        </>
                    )}
                </>
            )}
            {reportStatus === 'polling' && (
                <div style={{ textAlign: 'center', padding: 20 }}>
                    <LoadingOutlined style={{ fontSize: 32 }} spin />
                    <p>Đang tạo báo cáo... Vui lòng chờ.</p>
                </div>
            )}
            {reportStatus === 'success' && (
                <div style={{ textAlign: 'center', padding: 20 }}>
                    <CheckCircleOutlined style={{ fontSize: 32, color: '#52c41a' }} />
                    <p>Tải báo cáo thành công!</p>
                </div>
            )}
            {reportStatus === 'failed' && (
                <div style={{ textAlign: 'center', padding: 20 }}>
                    <CloseCircleOutlined style={{ fontSize: 32, color: '#ff4d4f' }} />
                    <p>{reportError}</p>
                    <Button onClick={() => { setReportStatus('idle'); setReportError('') }}>
                        Thử lại
                    </Button>
                </div>
            )}
            <Divider />
        </Modal>
    )
}

export default ReportDownloadModal
